<template>
  <section-wrapper title="Delivery Service Providers" :loading="isLoading">
    <b-table
      :data="merchantDeliveryServices"
      class="is-middle-aligned flex-grow"
      hoverable
      :mobile-cards="false"
    >
      <template #empty>
        <p class="has-text-centered pad is-size-5 has-text-grey-light">
          No Delivery Service Providers Configured
        </p>
      </template>

      <b-table-column v-slot="{ row }" sortable field="deliveryService.name" label="Name">
        {{ getDeliveryServiceName(row.deliveryServiceId) }}
      </b-table-column>

      <b-table-column
        v-slot="{ row }"
        centered
        sortable
        field="isActive"
        label="Available to Locations"
      >
        <b-icon
          :icon="row.isActive ? 'check' : 'times'"
          :type="row.isActive ? 'is-success' : 'is-danger'"
          size="is-small"
        />
      </b-table-column>

      <b-table-column
        v-slot="{ row }"
        centered
        sortable
        field="platformTypeId"
        label="Platform"
      >
        {{ platformTypesMap[row.platformTypeId] }}
      </b-table-column>

      <b-table-column
        v-slot="{ row }"
        centered
        cell-class="actions"
        label="Actions"
        width="1"
      >
        <div class="is-flex dist-x-xs">
          <b-button class="is-transparent" type="is-white" @click="openEditMerchantDeliveryServiceModal(row)">
            <b-icon icon="pencil" size="is-small" />
          </b-button>
          <b-button
            class="is-transparent"
            type="is-white"
            @click="openRemoveDeliveryServiceModal(row)"
          >
            <b-icon icon="trash-alt" type="is-danger" size="is-small" />
          </b-button>
        </div>
      </b-table-column>
    </b-table>
    <div class="pad-x pad-y-sm has-border-top has-border-grey-lighter">
      <b-button
        inverted
        class="is-transparent"
        type="is-primary"
        icon-left="plus"
        :loading="submittingDeliveryService"
        :disabled="!canAddDeliveryServices"
        @click="openAddDeliveryServiceModal()"
      >
        New Delivery Service Provider
      </b-button>
    </div>
  </section-wrapper>
</template>

<script>
  import AlertModal from '@/components/globals/alert-modal.vue';

  import capitalCase from '@/helpers/capitalCase';
  import platformTypes from '@/constants/platformTypes';

  import DeliveryService from '@/store/classes/DeliveryService';
  import MerchantDeliveryService from '@/store/classes/MerchantDeliveryService';

  import AddDeliveryServiceModal from './add-delivery-service-modal.vue';
  import EditMerchantDeliveryServiceModal from './edit-merchant-delivery-service-modal.vue';

  export default {
    name: 'MerchantDeliveryServices',

    props: {
      merchantId: {
        type: Number,
        default: null
      }
    },

    data() {
      return {
        platformTypesMap: {}
      };
    },

    computed: {
      isLoading() {
        return MerchantDeliveryService.$state().fetching || DeliveryService.$state().fetching;
      },

      submittingDeliveryService() {
        return MerchantDeliveryService.$state().submitting;
      },

      allDeliveryServiceOptions() {
        return DeliveryService.all();
      },

      canAddDeliveryServices() {
        return this.allDeliveryServiceOptions.length !== this.merchantDeliveryServices.length;
      },

      merchantDeliveryServices() {
        return MerchantDeliveryService
          .query()
          .where('merchantId', this.merchantId)
          .orderBy('name')
          .get();
      }
    },

    async created() {
      await this.onCreated();
    },


    methods: {
      async onCreated() {
        this.platformTypesMap = platformTypes.reduce((acc, platformType) => {
          acc[platformType.id] = platformType.name;
          return acc;
        }, {});
        await Promise.all([this.fetchDeliveryServices(), this.fetchMerchantDeliveryServices()]);
      },

      async fetchMerchantDeliveryServices() {
        try {
          await MerchantDeliveryService.fetchMerchantDeliveryServices(this.merchantId);
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: {
              message: 'There was an error fetching the merchant delivery services'
            },
            error
          });
        }
      },

      async fetchDeliveryServices() {
        try {
          await DeliveryService.fetchDeliveryServices();
        }
        catch (error) {
          this.$_onRequestError({
            toastOptions: {
              message: 'There was an error fetching the delivery service options'
            },
            error
          });
        }
      },

      getDeliveryServiceName(deliveryServiceId) {
        const deliveryService = this.allDeliveryServiceOptions.find(option => option.id === deliveryServiceId);
        return deliveryService ? capitalCase(deliveryService.name) : 'Name Unset';
      },

      openRemoveDeliveryServiceModal(deliveryService) {
        const deliveryServiceName = this.getDeliveryServiceName(deliveryService.deliveryServiceId);

        this.$buefy.modal.open({
          parent: this,
          component: AlertModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: ['escape', 'outside'],
          props: {
            icon: 'circle-times',
            title: 'Delete Delivery Service',
            message: `Are you sure you want to delete <b>${deliveryServiceName}</b> delivery service?`,
            type: 'is-danger',
            horizontal: true,
            showCloseButton: false,
            buttons: [
              { text: 'Cancel' },
              {
                text: 'Delete',
                primary: true,
                onClick: () => this.removeDeliveryService(deliveryService)
              }
            ]
          }
        });
      },

      openAddDeliveryServiceModal() {
        this.$buefy.modal.open({
          parent: this,
          component: AddDeliveryServiceModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          customClass: 'auto-width',
          props: {
            merchantDeliveryServices: this.merchantDeliveryServices,
            merchantId: this.merchantId
          }
        });
      },

      openEditMerchantDeliveryServiceModal(merchantDeliveryService) {
        const deliveryServiceName = this.getDeliveryServiceName(merchantDeliveryService.deliveryServiceId);

        this.$buefy.modal.open({
          parent: this,
          component: EditMerchantDeliveryServiceModal,
          hasModalCard: true,
          trapFocus: true,
          canCancel: false,
          customClass: 'auto-width',
          props: {
            merchantDeliveryService,
            deliveryServiceName,
            merchantId: this.merchantId
          }
        });
      },

      async removeDeliveryService(deliveryService) {
        const deliveryServiceName = this.getDeliveryServiceName(deliveryService.deliveryServiceId);

        try {
          await MerchantDeliveryService.removeMerchantDeliveryService({
            merchantId: this.merchantId,
            merchantDeliveryService: deliveryService
          });

          this.$_onRequestSuccess({
            toastOptions: {
              message: `Successfully removed <b>${deliveryServiceName}</b> delivery service!`
            }
          });
        }
        catch (error) {
          this.$_onRequestError({
            error,
            toastOptions: {
              message: `There was an error removing <b>${deliveryServiceName}</b> delivery service`
            }
          });
        }
      }
    }
  };
</script>
